/**
 * Fenster mit allen Filtern. Am Handy erscheint es von unten, am Rechner
 * als breite Karte. Die Trefferzahl wird laufend angezeigt.
 */
import { useEffect, useState } from 'react';
import type { Category, Tag } from '../api/types.ts';
import type { RecipeFilter } from '../lib/search.ts';
import { texts } from '../texts/de.ts';

/** Eigenschaften des Filterfensters. */
export interface FilterPanelProps {
  readonly filter: RecipeFilter;
  readonly categories: readonly Category[];
  readonly tags: readonly Tag[];
  readonly resultCount: number;
  readonly onApply: (filter: RecipeFilter) => void;
  readonly onClose: () => void;
}

/** Schaltet einen Wert in einer Liste um. */
function toggle<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

/** Zeitstufen für die Auswahl. */
const TIME_STEPS = [15, 30, 45, 60, 90, 120] as const;

/** Wandelt eine mit Komma getrennte Eingabe in eine Liste. */
function toList(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

/** Filterfenster. */
export function FilterPanel({ filter, categories, tags, resultCount, onApply, onClose }: FilterPanelProps) {
  const [draft, setDraft] = useState<RecipeFilter>(filter);

  useEffect(() => {
    setDraft(filter);
  }, [filter]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 md:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={texts.filter.title}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-surface-raised p-5 md:rounded-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{texts.filter.title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={texts.filter.close}
            className="rounded-lg border border-border-subtle px-2 py-1 text-content-muted"
          >
            ✕
          </button>
        </div>

        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium">{texts.filter.difficulty}</legend>
          <div className="flex gap-2">
            {(['easy', 'medium', 'hard'] as const).map((difficulty) => (
              <button
                key={difficulty}
                type="button"
                onClick={() => setDraft({ ...draft, difficulties: toggle(draft.difficulties, difficulty) })}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  draft.difficulties.includes(difficulty)
                    ? 'border-accent bg-accent text-on-accent'
                    : 'border-border-subtle text-content-muted'
                }`}
              >
                {difficulty === 'easy' ? 'Leicht' : difficulty === 'medium' ? 'Mittel' : 'Schwer'}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="mb-1 block text-sm font-medium">{texts.filter.maxTotalTime}</label>
        <select
          value={draft.maxTotalMinutes}
          onChange={(event) => setDraft({ ...draft, maxTotalMinutes: Number(event.target.value) })}
          className="mb-4 w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
        >
          <option value={0}>ohne Grenze</option>
          {TIME_STEPS.map((step) => (
            <option key={step} value={step}>
              bis {step} Minuten
            </option>
          ))}
        </select>

        <label className="mb-1 block text-sm font-medium">{texts.filter.maxActiveTime}</label>
        <select
          value={draft.maxActiveMinutes}
          onChange={(event) => setDraft({ ...draft, maxActiveMinutes: Number(event.target.value) })}
          className="mb-4 w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
        >
          <option value={0}>ohne Grenze</option>
          {TIME_STEPS.map((step) => (
            <option key={step} value={step}>
              bis {step} Minuten
            </option>
          ))}
        </select>

        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium">{texts.filter.rating}</legend>
          <div className="mb-2 flex flex-wrap gap-2">
            {[0, 1, 2, 3, 4, 5].map((stars) => (
              <button
                key={stars}
                type="button"
                onClick={() => setDraft({ ...draft, minRating: stars, unratedOnly: false })}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  draft.minRating === stars && !draft.unratedOnly
                    ? 'border-accent bg-accent text-on-accent'
                    : 'border-border-subtle text-content-muted'
                }`}
              >
                {stars === 0 ? 'egal' : `${stars}★`}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={!draft.ratingByMe}
                onChange={() => setDraft({ ...draft, ratingByMe: false })}
              />
              {texts.filter.ratingAverage}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={draft.ratingByMe}
                onChange={() => setDraft({ ...draft, ratingByMe: true })}
              />
              {texts.filter.ratingMine}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.unratedOnly}
                onChange={() => setDraft({ ...draft, unratedOnly: !draft.unratedOnly })}
              />
              unbewertet
            </label>
          </div>
        </fieldset>

        {tags.length > 0 ? (
          <fieldset className="mb-4">
            <legend className="mb-2 text-sm font-medium">{texts.filter.tags}</legend>
            <div className="mb-2 flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={!draft.tagsAll}
                  onChange={() => setDraft({ ...draft, tagsAll: false })}
                />
                {texts.filter.tagsAny}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={draft.tagsAll}
                  onChange={() => setDraft({ ...draft, tagsAll: true })}
                />
                {texts.filter.tagsAll}
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => setDraft({ ...draft, tagNames: toggle(draft.tagNames, tag.name) })}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    draft.tagNames.includes(tag.name)
                      ? 'border-accent bg-accent text-on-accent'
                      : 'border-border-subtle text-content-muted'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}

        {categories.length > 0 ? (
          <fieldset className="mb-4">
            <legend className="mb-2 text-sm font-medium">{texts.recipes.categories}</legend>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setDraft({ ...draft, categoryIds: toggle(draft.categoryIds, category.id) })}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    draft.categoryIds.includes(category.id)
                      ? 'border-accent bg-accent text-on-accent'
                      : 'border-border-subtle text-content-muted'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}

        <fieldset className="mb-5">
          <legend className="mb-2 text-sm font-medium">{texts.filter.ingredients}</legend>
          <label className="mb-1 block text-xs text-content-muted">{texts.filter.with}</label>
          <input
            value={draft.withIngredients.join(', ')}
            onChange={(event) => setDraft({ ...draft, withIngredients: toList(event.target.value) })}
            placeholder="Zucchini, Feta"
            className="mb-3 w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
          />
          <label className="mb-1 block text-xs text-content-muted">{texts.filter.without}</label>
          <input
            value={draft.withoutIngredients.join(', ')}
            onChange={(event) => setDraft({ ...draft, withoutIngredients: toList(event.target.value) })}
            placeholder="Nüsse"
            className="w-full rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm"
          />
        </fieldset>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setDraft(filter)}
            className="rounded-xl border border-border-subtle px-4 py-2.5 text-sm"
          >
            {texts.recipes.clearFilters}
          </button>
          <button
            type="button"
            onClick={() => onApply(draft)}
            className="flex-1 rounded-xl bg-accent px-4 py-2.5 font-semibold text-on-accent"
          >
            {resultCount} {texts.filter.apply}
          </button>
        </div>
      </div>
    </div>
  );
}
